import {FC} from 'react';
import {Box, Typography, Alert} from '@mui/material';
import {
    parseMagicCardSummary,
    type MagicCardSummary,
    type MorphologyItem,
    type MagicCardExampleItem,
    type SimilarWordItem,
} from '../../utils/magicCardSummary';

interface Props {
    summaryJson: string | null;
}

export const MagicCardContentTab: FC<Props> = ({summaryJson}) => {
    if (!summaryJson) {
        return (
            <Alert severity="info" variant="outlined">
                전역 캐시가 없어 표시할 매직카드 내용이 없습니다.
            </Alert>
        );
    }

    const summary = parseMagicCardSummary(summaryJson);
    if (!summary) {
        return (
            <Alert severity="warning" variant="outlined">
                summary_json 파싱에 실패했습니다. "메타 & 유저 상태" 탭의 원본 JSON을 확인하세요.
            </Alert>
        );
    }

    const allEmpty =
        summary.morphology.length === 0 &&
        summary.collocations.length === 0 &&
        summary.examples.length === 0 &&
        summary.similarWords.length === 0;

    if (allEmpty) {
        return (
            <Alert severity="info" variant="outlined">
                매직카드 4블록이 모두 비어 있습니다.
            </Alert>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2.5}>
            <Header summary={summary}/>
            {summary.morphology.length > 0 && (
                <Block title="형태확장">
                    {summary.morphology.map((m, i) => <MorphRow key={i} item={m}/>)}
                </Block>
            )}
            {summary.collocations.length > 0 && (
                <Block title="자주 쓰는 표현">
                    {summary.collocations.map((c, i) => <BulletRow key={i} text={c}/>)}
                </Block>
            )}
            {summary.examples.length > 0 && (
                <Block title="예문">
                    {summary.examples.map((ex, i) => <ExampleRow key={i} item={ex}/>)}
                </Block>
            )}
            {summary.similarWords.length > 0 && (
                <Block title="비슷한 말">
                    {summary.similarWords.map((sw, i) => <SimilarRow key={i} item={sw}/>)}
                </Block>
            )}
        </Box>
    );
};

const Header: FC<{summary: MagicCardSummary}> = ({summary}) => (
    <Box>
        {summary.word && (
            <Typography variant="h6" fontWeight={700}>{summary.word}</Typography>
        )}
        {summary.primaryMeaning && (
            <Typography variant="body2" color="text.secondary">{summary.primaryMeaning}</Typography>
        )}
    </Box>
);

const Block: FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
    <Box>
        <Typography variant="caption" color="primary" fontWeight={700} sx={{letterSpacing: 0.2}}>
            {title}
        </Typography>
        <Box mt={0.5}>{children}</Box>
    </Box>
);

const MorphRow: FC<{item: MorphologyItem}> = ({item}) => (
    <Typography variant="body2" sx={{py: 0.3}}>
        • <Box component="span" fontWeight={700}>{item.text}</Box>
        {item.pos && (
            <Box component="span" color="text.secondary" sx={{fontSize: '0.75rem', ml: 0.5}}>
                ({item.pos})
            </Box>
        )}
        {item.meaning && (
            <Box component="span" color="text.secondary" sx={{ml: 1}}>
                {item.meaning}
            </Box>
        )}
    </Typography>
);

const BulletRow: FC<{text: string}> = ({text}) => (
    <Typography variant="body2" sx={{py: 0.3}}>• {text}</Typography>
);

const ExampleRow: FC<{item: MagicCardExampleItem}> = ({item}) => (
    <Box sx={{py: 0.5}}>
        <Typography variant="body2" fontWeight={500}>{item.sentence}</Typography>
        {item.translation && (
            <Typography variant="caption" color="text.secondary">{item.translation}</Typography>
        )}
    </Box>
);

const SimilarRow: FC<{item: SimilarWordItem}> = ({item}) => (
    <Typography variant="body2" sx={{py: 0.3}}>
        • <Box component="span" fontWeight={700}>{item.text}</Box>
        {item.meaning && (
            <Box component="span" color="text.secondary" sx={{ml: 1}}>{item.meaning}</Box>
        )}
        {item.note && (
            <Box component="span" color="text.secondary" sx={{ml: 1, fontStyle: 'italic', fontSize: '0.75rem'}}>
                — {item.note}
            </Box>
        )}
    </Typography>
);
